import axios from "@/plugins/axios";
import storeUtils from "@/stores/utils";
import $utils from "@/utils";
import { FORMAT_SELECT_CUSTOM } from "@/constants/format-select";
import products from "./submodules/products";

const STEP_SELECT = "select";
const STEP_PIECE_CONTENT = "piece_content";

function findMatchingProductFormat(formats, quantity, unit) {
    if (!quantity || !unit) {
        return null;
    }
    const normalizedQty = $utils.formatDecimalInputValue(quantity, 3);
    return (
        formats.find(
            (format) =>
                format.content_unit === unit
                && $utils.formatDecimalInputValue(format.content_quantity, 3)
                    === normalizedQty,
        ) || null
    );
}

function resolveInitialProductFormatId(item, formats) {
    if (item.format_id) {
        return item.format_id;
    }
    if (item.format_data?.id) {
        return item.format_data.id;
    }
    const unitContent = $utils.getProductUnitContent(item);
    const quantity = item.piece_content?.quantity ?? (
        unitContent ? $utils.formatDecimalInputValue(unitContent.qty, 3) : null
    );
    const unit = item.piece_content?.unit ?? unitContent?.unit;
    if (quantity && unit) {
        const match = findMatchingProductFormat(formats, quantity, unit);
        if (match) {
            return match.id;
        }
        return FORMAT_SELECT_CUSTOM;
    }
    return null;
}

function applyProductFormatToForm(form, formatId, formats) {
    if (formatId === FORMAT_SELECT_CUSTOM) {
        form.product_format_id = FORMAT_SELECT_CUSTOM;
        form.custom = true;
        return;
    }
    const format = formats.find((entry) => entry.id === formatId);
    form.product_format_id = formatId;
    form.custom = false;
    if (format?.content_quantity != null && format?.content_unit) {
        form.quantity = $utils.formatDecimalInputValue(
            format.content_quantity,
            3,
        );
        form.unit = format.content_unit;
    }
}

function validateOnePieceContentForm(state, productId) {
    const form = state.piece_content_forms[productId];
    if (!form) {
        return {};
    }
    const item = state.selected_items.find(
        (selected) => String(selected.id) === String(productId),
    );
    const fieldErrors = {};
    const isCustom =
        form.custom || form.product_format_id === FORMAT_SELECT_CUSTOM;

    if (!form.product_format_id) {
        fieldErrors.product_format = ["pieceContentFormatRequired"];
    } else if (isCustom) {
        if (!form.quantity || Number(form.quantity) <= 0) {
            fieldErrors.quantity = ["pieceContentQuantityRequired"];
        }
        if (!form.unit) {
            fieldErrors.unit = ["pieceContentUnitRequired"];
        }
    } else if (!form.quantity || Number(form.quantity) <= 0 || !form.unit) {
        fieldErrors.product_format = ["pieceContentFormatRequired"];
    }

    if (
        Object.keys(fieldErrors).length === 0
        && item
        && form.product_format_id
    ) {
        const compatible = $utils.pieceContentFormCompatibleWithAddToCatalogSaleFormat(
            item,
            item.add_format,
            state.formats,
            form,
        );
        if (!compatible) {
            if (isCustom) {
                fieldErrors.quantity = ["pieceContentIncompatibleWithSaleFormat"];
                fieldErrors.unit = ["pieceContentIncompatibleWithSaleFormat"];
            } else {
                fieldErrors.product_format = ["pieceContentIncompatibleWithSaleFormat"];
            }
        }
    }

    return fieldErrors;
}

function refreshPieceContentFormErrors(state, productId) {
    if (!state.piece_content_forms[productId]) {
        return;
    }
    state.piece_content_forms[productId].errors = validateOnePieceContentForm(
        state,
        productId,
    );
}

const state = {
    catalog: null,
    /** When set, product list is restricted to this provider and the provider filter is hidden */
    locked_provider: null,
    selected_items: [],
    adding_products: false,
    saving_piece_content: false,
    step: STEP_SELECT,
    piece_content_forms: {},
    formats: [],
    formats_loading: false,
};

function itemNeedsPieceContent(item, formats) {
    if ($utils.getPieceContent(item)) {
        return false;
    }
    return $utils.productNeedsPieceContentForAddToCatalog(
        item,
        item.add_format,
        formats,
    );
}

const getters = {
    itemsNeedingPieceContent(state) {
        return state.selected_items.filter((item) =>
            itemNeedsPieceContent(item, state.formats),
        );
    },
};

const mutations = {
    updateCatalog(state, catalog) {
        state.catalog = catalog;
    },
    updateLockedProvider(state, provider) {
        state.locked_provider = provider;
    },
    updateStep(state, step) {
        state.step = step;
    },
    selectItem(state, item) {
        if (!state.selected_items.find((selected_item) => selected_item.id == item.id)) {
            state.selected_items.push({
                ...item,
                add_format: null,
                add_unit_amount: "",
            });
        }
    },
    deselectItem(state, item) {
        state.selected_items = state.selected_items.filter(
            (selected_item) => selected_item.id != item.id,
        );
    },
    toggleItem(state, item) {
        if (!state.selected_items.find((selected_item) => selected_item.id == item.id)) {
            state.selected_items.push({
                ...item,
                add_format: null,
                add_unit_amount: "",
            });
        } else {
            state.selected_items = state.selected_items.filter(
                (selected_item) => selected_item.id != item.id,
            );
        }
    },
    updateSelectedItems(state, items) {
        state.selected_items = (items || []).map((item) => ({
            ...item,
            add_format: item.add_format ?? item.add_sale_format ?? null,
            add_unit_amount: item.add_unit_amount ?? "",
        }));
    },
    updateSelectedItemFormat(state, { productId, formatId }) {
        const item = state.selected_items.find((selected) => selected.id === productId);
        if (item) {
            item.add_format = formatId;
        }
    },
    updateSelectedItemUnitAmount(state, { productId, unitAmount }) {
        const item = state.selected_items.find((selected) => selected.id === productId);
        if (item) {
            item.add_unit_amount = unitAmount;
        }
    },
    updateFormats(state, formats) {
        state.formats = $utils.sortFormatsByCapacity(formats);
    },
    appendFormat(state, format) {
        if (!format?.id) {
            return;
        }
        if (!state.formats.some((entry) => entry.id === format.id)) {
            state.formats.push(format);
        }
        state.formats = $utils.sortFormatsByCapacity(state.formats);
    },
    updateFormatsLoading(state, loading) {
        state.formats_loading = loading;
    },
    updateAddingProducts(state, adding) {
        state.adding_products = adding;
    },
    updateSavingPieceContent(state, saving) {
        state.saving_piece_content = saving;
    },
    initPieceContentForms(state) {
        const forms = {};
        state.selected_items.forEach((item) => {
            if (!itemNeedsPieceContent(item, state.formats)) {
                return;
            }
            const existing = item.piece_content;
            const unitContent = $utils.getProductUnitContent(item);
            const initialFormatId = resolveInitialProductFormatId(
                item,
                state.formats,
            );
            const unitOptions = $utils
                .getPieceContentUnitOptionsForAddToCatalogSaleFormat(
                    item.add_format,
                    state.formats,
                )
                .map((opt) => opt.value);
            const resolvedQuantity = existing?.quantity
                ?? (unitContent
                    ? $utils.formatDecimalInputValue(unitContent.qty, 3)
                    : "");
            const resolvedUnit = existing?.unit ?? unitContent?.unit ?? null;
            const form = {
                product_format_id: initialFormatId,
                custom: initialFormatId === FORMAT_SELECT_CUSTOM,
                quantity: resolvedQuantity
                    ? $utils.formatDecimalInputValue(resolvedQuantity, 3)
                    : "",
                unit:
                    resolvedUnit && unitOptions.includes(resolvedUnit)
                        ? resolvedUnit
                        : null,
                errors: {},
            };
            if (
                initialFormatId
                && initialFormatId !== FORMAT_SELECT_CUSTOM
            ) {
                applyProductFormatToForm(form, initialFormatId, state.formats);
            }
            forms[item.id] = form;
        });
        state.piece_content_forms = forms;
        Object.keys(forms).forEach((productId) => {
            refreshPieceContentFormErrors(state, productId);
        });
    },
    updatePieceContentProductFormat(state, { productId, formatId }) {
        const form = state.piece_content_forms[productId];
        if (!form) {
            return;
        }
        applyProductFormatToForm(form, formatId, state.formats);
        refreshPieceContentFormErrors(state, productId);
    },
    applyBulkPieceContentProductFormat(state, { formatId, onlyEmpty }) {
        Object.keys(state.piece_content_forms).forEach((productId) => {
            const form = state.piece_content_forms[productId];
            if (
                onlyEmpty
                && form.product_format_id != null
                && form.product_format_id !== ""
            ) {
                return;
            }
            applyProductFormatToForm(form, formatId, state.formats);
            refreshPieceContentFormErrors(state, productId);
        });
    },
    updatePieceContentForm(state, { productId, field, value }) {
        if (!state.piece_content_forms[productId]) {
            return;
        }
        state.piece_content_forms[productId][field] = value;
        refreshPieceContentFormErrors(state, productId);
    },
    applyBulkPieceContentUnit(state, { unit, onlyEmpty = false }) {
        Object.keys(state.piece_content_forms).forEach((productId) => {
            const form = state.piece_content_forms[productId];
            if (onlyEmpty && form.unit) {
                return;
            }
            form.unit = unit;
            refreshPieceContentFormErrors(state, productId);
        });
    },
    applyBulkPieceContentQuantity(state, { quantity, onlyEmpty }) {
        Object.keys(state.piece_content_forms).forEach((productId) => {
            const form = state.piece_content_forms[productId];
            if (onlyEmpty && form.quantity) {
                return;
            }
            form.quantity = quantity;
            refreshPieceContentFormErrors(state, productId);
        });
    },
    setPieceContentFormErrors(state, { productId, errors }) {
        if (state.piece_content_forms[productId]) {
            state.piece_content_forms[productId].errors = errors;
        }
    },
    syncPieceContentOnSelectedItems(state) {
        state.selected_items = state.selected_items.map((item) => {
            const form = state.piece_content_forms[item.id];
            if (!form?.quantity || !form?.unit) {
                return item;
            }
            return {
                ...item,
                piece_content: {
                    quantity: form.quantity,
                    unit: form.unit,
                },
                piece_content_quantity: form.quantity,
                piece_content_unit: form.unit,
            };
        });
    },
};

const actions = {
    async fetchFormats({ commit }) {
        commit("updateFormatsLoading", true);
        try {
            const response = await axios.get("/api/formats/", {
                params: { limit: 10000 },
            });
            commit(
                "updateFormats",
                response.data.results || response.data || [],
            );
        } catch (xhr_error) {
            console.error("Error loading formats:", xhr_error);
            commit("updateFormats", []);
        } finally {
            commit("updateFormatsLoading", false);
        }
    },

    validateSelectedItems() {
        return [];
    },

    validatePieceContentForms({ state, commit }) {
        const errors = [];
        Object.keys(state.piece_content_forms).forEach((productId) => {
            const fieldErrors = validateOnePieceContentForm(state, productId);
            commit("setPieceContentFormErrors", {
                productId,
                errors: fieldErrors,
            });
            if (Object.keys(fieldErrors).length > 0) {
                const item = state.selected_items.find(
                    (selected) => String(selected.id) === String(productId),
                );
                errors.push({
                    productId,
                    productName: item?.name || productId,
                    fieldErrors,
                });
            }
        });
        return errors;
    },

    async savePieceContents({ commit, dispatch, state }) {
        const validationErrors = await dispatch("validatePieceContentForms");
        if (validationErrors.length > 0) {
            const error = new Error("validation");
            error.validationErrors = validationErrors;
            error.validationType = "pieceContent";
            throw error;
        }

        commit("updateSavingPieceContent", true);
        try {
            const productIds = Object.keys(state.piece_content_forms);
            await Promise.all(
                productIds.map(async (productId) => {
                    const form = state.piece_content_forms[productId];
                    const payload = {
                        piece_content_quantity: form.quantity,
                        piece_content_unit: form.unit,
                    };
                    if (
                        form.product_format_id
                        && form.product_format_id !== FORMAT_SELECT_CUSTOM
                    ) {
                        payload.format = form.product_format_id;
                    }
                    await axios.patch(`/api/products/${productId}/`, payload);
                }),
            );
            commit("syncPieceContentOnSelectedItems");
        } catch (xhr_error) {
            throw storeUtils.handleError(xhr_error);
        } finally {
            commit("updateSavingPieceContent", false);
        }
    },

    async proceedToAdd({ commit, dispatch, getters: localGetters }) {
        const validationErrors = await dispatch("validateSelectedItems");
        if (validationErrors.length > 0) {
            const error = new Error("validation");
            error.validationErrors = validationErrors;
            throw error;
        }

        if (localGetters.itemsNeedingPieceContent.length > 0) {
            commit("initPieceContentForms");
            commit("updateStep", STEP_PIECE_CONTENT);
            return { step: STEP_PIECE_CONTENT };
        }

        await dispatch("importProducts");
        return { step: "done" };
    },

    async savePieceContentAndImport({ commit, dispatch }) {
        await dispatch("savePieceContents");
        await dispatch("importProducts");
        commit("updateStep", STEP_SELECT);
        return { step: "done" };
    },

    goBackToSelection({ commit }) {
        commit("updateStep", STEP_SELECT);
    },

    async importProducts({ commit, dispatch, state }) {
        const validationErrors = await dispatch("validateSelectedItems");
        if (validationErrors.length > 0) {
            const error = new Error("validation");
            error.validationErrors = validationErrors;
            throw error;
        }

        commit("updateAddingProducts", true);
        try {
            const product_configs = {};
            state.selected_items.forEach((item) => {
                product_configs[item.id] = {
                    format: item.add_format || null,
                    unit_amount: item.add_unit_amount || null,
                };
            });
            const response = await axios.post(`/api/products/bulk/`, {
                action: "add_to_catalog",
                instances: state.selected_items.map((item) => item.id),
                payload: {
                    catalog: state.catalog.id,
                    product_configs,
                },
            });
            return response;
        } catch (xhr_error) {
            throw storeUtils.handleError(xhr_error);
        } finally {
            commit("updateAddingProducts", false);
        }
    },

    async init({ commit, dispatch }, params) {
        commit("updateCatalog", params.catalog);
        commit("updateSelectedItems", []);
        commit("updateStep", STEP_SELECT);
        commit("updateLockedProvider", params.provider ?? null);

        const filters = {
            limit: 20,
            for_catalog: params.catalog.id,
        };
        if (params.provider?.id != null) {
            filters.provider = params.provider.id;
        } else if (params.catalog.provider == null) {
            // Plain sell catalog: only offer the entity's own products.
            // Without this, the default /api/products/ scope also returns
            // any product stored in the entity's warehouses (provider
            // products included).
            filters.owned = true;
        }

        dispatch("products/init", { filters });
        dispatch("products/fetchProducts");
        dispatch("fetchFormats");
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
    modules: {
        products: products(),
    },
};
